import type { FilterPill } from '@/app/common/filters/surfaces/bar/pills/types.ts';
import { isFilterSelected } from '@/app/common/filters/core/filterSelectedUtils.ts';
import { quickFilterById, quickFilterTargetOf } from '@/app/common/filters/quickFilters/quickFilterCatalogue.ts';
import { isQuickFilterApplied } from '@/app/common/filters/quickFilters/quickFilterSelection.ts';

type OrderEvoFilterBarPillsProps = {
  pills: FilterPill[];
  quickFilterPills: FilterPill[];
  groupOrder: string[];
};

const hasSelection = (pill: FilterPill) => pill.filters.some(isFilterSelected);

const isQuickFilterPillApplied = (pill: FilterPill): boolean => {
  const quickFilter = pill.quickFilterId ? quickFilterById(pill.quickFilterId) : undefined;
  const filter = pill.filters[0];
  const target = quickFilter && filter ? quickFilterTargetOf(quickFilter, filter) : undefined;
  if (!filter || !target) return false;

  return isQuickFilterApplied(filter, target.values);
};

export const orderEvoFilterBarPills = ({
  pills,
  quickFilterPills,
  groupOrder,
}: OrderEvoFilterBarPillsProps): FilterPill[] => {
  const pinned = pills.filter((pill) => pill.pinned);
  const groups = pills.filter((pill) => !pill.pinned);

  const active = groups.filter(hasSelection);
  const inactive = groups.filter((pill) => !hasSelection(pill));
  const activeIds = new Set(active.map((pill) => pill.id as string));

  const rank = (pill: FilterPill) => groupOrder.indexOf(pill.id as string);
  const orderedActive = [...active].sort((a, b) => {
    const rankA = rank(a);
    const rankB = rank(b);
    if (rankA === rankB) return 0;
    if (rankA === -1) return 1;
    if (rankB === -1) return -1;
    return rankB - rankA;
  });

  const aheadOfQuick = inactive.filter((pill) => pill.leadsBar);
  const others = inactive.filter((pill) => !pill.leadsBar);
  const quick = quickFilterPills.filter((pill) => !activeIds.has(pill.id as string) && !isQuickFilterPillApplied(pill));

  return [...pinned, ...orderedActive, ...aheadOfQuick, ...quick, ...others];
};
